import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui";
import { formatCurrency } from "@/lib/utils";
import type { HourlyRevenue } from "@/types";

interface HourlyRevenueChartProps {
  data: HourlyRevenue[];
  peakHour: HourlyRevenue | null;
}

export function HourlyRevenueChart({
  data,
  peakHour,
}: HourlyRevenueChartProps) {
  const byHour = new Map(data.map((row) => [row.hour, row]));
  const maxRevenue = Math.max(0, ...data.map((row) => row.revenue));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hourly traffic</CardTitle>
        <CardDescription>
          {peakHour
            ? `Peak at ${formatHour(peakHour.hour)} with ${formatCurrency(peakHour.revenue)} from ${peakHour.orders} orders`
            : "No order activity yet"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex h-40 items-end gap-1">
          {Array.from({ length: 24 }, (_, hour) => {
            const row = byHour.get(hour);
            const revenue = row?.revenue ?? 0;
            const height =
              maxRevenue > 0 ? Math.max(2, (revenue / maxRevenue) * 100) : 2;
            const isPeak = peakHour?.hour === hour && revenue > 0;

            return (
              <div
                key={hour}
                className="flex flex-1 flex-col items-center gap-1"
                title={`${formatHour(hour)} · ${formatCurrency(revenue)} · ${row?.orders ?? 0} orders`}
              >
                <div
                  className={
                    isPeak
                      ? "w-full rounded-t bg-primary"
                      : "w-full rounded-t bg-primary/30"
                  }
                  style={{ height: `${height}%` }}
                />
              </div>
            );
          })}
        </div>
        <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
          <span>00</span>
          <span>06</span>
          <span>12</span>
          <span>18</span>
          <span>23</span>
        </div>
      </CardContent>
    </Card>
  );
}

function formatHour(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`;
}
